<script lang="ts" setup>

    const props = defineProps<{
        defaultDisplayCount: number,
        storeyFilter: string[]
    }>();

    let homeDesigns: any = await useAllHomeDesigns();
    homeDesigns = homeDesigns.value;

    let storeyFilter = props?.storeyFilter?.length > 0 ? props?.storeyFilter[0] : 'all';

    if (storeyFilter !== 'all') {
        homeDesigns = homeDesigns.filter((design: any) => {
            const designStoreys = design?.body?.homeDesignDetails?.homeDesignVariations
                ?.map((v: any) => v?.storeys?.toString().toLowerCase())
                .flat() || [];
            return designStoreys.includes(storeyFilter);
        });
    }

    let defaultDisplayCount = props?.defaultDisplayCount > homeDesigns.length ? homeDesigns.length : props?.defaultDisplayCount;

    let blocks: number[] = [];
    let sizes: number[] = [];

    homeDesigns.forEach((design: any) => {
        design?.body?.homeDesignDetails?.homeDesignVariations?.forEach((variation: any)=> {
            blocks.push(variation?.landWidth);
            sizes.push(variation?.totalFloorAreaSqm);
        });
    });

    const maxBlocks = Math.ceil(Math.max(...blocks));
    const maxSize = Math.ceil(Math.max(...sizes));

    let currentWidth = maxBlocks;
    
    const additionalPostsPerPage = 3;
    const isLoading = ref(false);
    const visibleCount = ref(defaultDisplayCount);
    const visiblePosts = computed(() => {
        return homeDesigns.slice(0, visibleCount.value);
    });

    const loadMore = () => {
        if (visibleCount.value < homeDesigns.length && !isLoading.value) {

            isLoading.value = true;

            setTimeout(() => {
                visibleCount.value += additionalPostsPerPage;
                isLoading.value = false;
            }, 500);
        }
    }

    const hasMorePosts = computed(() => {
        return homeDesigns.length > visibleCount.value;
    });

    const resetFilters = () => {
        currentWidth = maxBlocks;

        if (import.meta.client) {
            const blockWidthInput = document.querySelector('#filter-block-width') as HTMLInputElement;
            const houseSizeInput = document.querySelector('#filter-house-size') as HTMLInputElement;
            const bedInput = document.querySelector('#filter-bed') as HTMLSelectElement;
            const bathInput = document.querySelector('#filter-bath') as HTMLSelectElement;
            const carInput = document.querySelector('#filter-car') as HTMLSelectElement;
            const storeysInput = document.querySelector('#filter-storeys') as HTMLSelectElement;

            if (blockWidthInput) {
                blockWidthInput.value = maxBlocks.toString();
                const blockTargetElement = document.getElementById('block-input-range-value');
                if (blockTargetElement) {
                    blockTargetElement.innerText = maxBlocks.toString();
                }
            }

            if (houseSizeInput) {
                houseSizeInput.value = maxSize.toString();
                const sizeTargetElement = document.getElementById('size-input-range-value');
                if (sizeTargetElement) {
                    sizeTargetElement.innerText = maxSize.toString();
                }
            }

            if (bedInput) bedInput.value = '-';
            if (bathInput) bathInput.value = '-';
            if (carInput) carInput.value = '-';
            if (storeysInput) storeysInput.value = storeyFilter !== 'all' ? storeyFilter : '-';

            document.querySelectorAll('.house-item').forEach(item => {
                item.classList.remove('d-none');
            });

            const noResultsElement = document.querySelector('#our-homes-no-results');
            if (noResultsElement) {
                noResultsElement.classList.add('d-none');
            }

        }
    };

</script>
<template>
    <div class="container-xl home-designs">
        <div class="form-filter-wrapper w-100 d-flex">
            <form class="row w-100">
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <label for="filter-block-width" class="form-label mb-0">Block Width (m)</label>
                <input type="range" class="form-range home-design-filter" min="1" step="1" id="filter-block-width"
                        data-target="block-input-range-value"
                        :max="Math.ceil(Math.max(...blocks))"
                        :value="currentWidth">
                <div class="w-100 d-flex justify-content-between align-items-center">
                    <span class="text-dark">1</span>
                    <span class="text-dark" id="block-input-range-value">{{currentWidth}}</span>
                </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <label for="filter-house-size" class="form-label mb-0">House Size (M2)</label>
                <input type="range" class="form-range home-design-filter" min="1" step="1" id="filter-house-size"
                        data-target="size-input-range-value"
                        :max="Math.ceil(Math.max(...sizes))"
                        :value="maxSize">
                <div class="w-100 d-flex justify-content-between align-items-center">
                    <span class="text-dark">1</span>
                    <span class="text-dark" id="size-input-range-value">{{maxSize}}</span>
                </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <div id="bed" style="display: none;">Bed</div>
                <select class="bg-transparent px-0 form-select rounded-0 shadow-none" id="filter-bed" aria-labelledby="bed">
                    <option value="-" selected>Bed</option>
                    <option value="3">3 Bed Rooms</option>
                    <option value="4">4 Bed Rooms</option>
                    <option value="5">5 Bed Rooms</option>
                </select>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <div id="bath" style="display: none;">Bath</div>
                <select class="bg-transparent px-0 form-select rounded-0 shadow-none" id="filter-bath" aria-labelledby="bath">
                    <option value="-" selected>Bath</option>
                    <option value="1">1 Bathrooms</option>
                    <option value="2">2 Bathrooms</option>
                    <option value="3">3 Bathrooms</option>
                    <option value="4">4 Bathrooms</option>
                    <option value="5">5 Bathrooms</option>
                </select>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <div id="car" style="display: none;">Car</div>
                <select class="bg-transparent px-0 form-select rounded-0 shadow-none" id="filter-car" aria-labelledby="car">
                    <option value="-" selected>Car</option>
                    <option value="1">1 Car Space</option>
                    <option value="2">2 Car Spaces</option>
                    <option value="3">3 Car Spaces</option>
                </select>
                </div>
                <div v-if="storeyFilter === 'all'" class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                <div id="storeys" style="display: none;">Storeys</div>
                <select class="bg-transparent px-0 form-select rounded-0 shadow-none" id="filter-storeys" aria-labelledby="storeys">
                    <option value="-" selected>Storeys</option>
                    <option value="single">Single</option>
                    <option value="double">Double</option>
                </select>
                </div>
                <div v-else class="col-12 col-md-6 col-lg-4 col-xl-2 dropdown-filter">
                    <div id="storeys" style="display: none;">Storeys</div>
                    <select class="bg-transparent px-0 form-select rounded-0 shadow-none" id="filter-storeys" aria-labelledby="storeys">
                        <option :value="storeyFilter" selected>{{ storeyFilter.charAt(0).toUpperCase() + storeyFilter.slice(1) }}</option>
                    </select>
                </div>
            </form>
            <div class="reset-filter-wrapper">
                <button type="button"
                        class="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 rounded-pill px-3"
                        @click="resetFilters"
                        id="reset-filters-btn"
                        title="Reset Filters"
                        aria-label="Reset Filters">
                    <Icon name="fa6-solid:rotate-left" size="14px"/>
                </button>
            </div>
        </div>
        <div class="design-wrapper row d-flex align-items-stretch g-md-5 mt-md-2 mt-5">
            <template v-for="homeDesign in visiblePosts">
                <HomeDesignItem :item="homeDesign.body" />
            </template>
        </div>
        <div class="section-spacer"></div>
        <div v-if="hasMorePosts" class="btn-action d-flex align-items-center justify-content-center">
            <button class="btn btn-primary rounded-pill" @click="loadMore" :disabled="isLoading">
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isLoading ? 'LOADING...' : 'LOAD MORE' }}
            </button>
        </div>
        <div class="section-spacer"></div>
    </div>
</template>